class PageHeaderSection extends HTMLElement {
  constructor() {
    super();

    this.isSticky = this.dataset.sticky === "true";
    this.isStickyAlways = this.dataset.stickyAlways === "true";
    this.isTransparent = this.dataset.transparent === "true";
    this.isAlwaysMobileMenu = this.dataset.alwaysMobileMenu === "true";

    this.classBodyAlwaysMobileMenu = "mobile-nav";
    this.mainFirstHeroBodyClass = "main-first-sticky-header-hero";

    this.header = document.querySelector(".page-header");
    this.desktopMenuTrigger = document.querySelector(
      ".wt-header__sticky-menu-trigger",
    );

    this.enabledClass = "sticky-enabled";
    this.showClass = "sticky-show";
    this.desktopHeaderWithMenuBarClass = "page-header--sticky-show-menubar-lg";
    this.initAbort = null;
    this.heroOverlapResizeObserver = null;
    this.heroOverlapSection = null;
    this._heroFirstMainSectionCached = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.initAbort?.abort();
    this.initAbort = null;
    this.removeAttribute("data-header-solid-bar");
    this.clearHeroPullUnderHeader();
    this.disableStickyHeader();
    this.cleanupAutoTransparentHero();
    document.body.classList.remove(this.mainFirstHeroBodyClass);
    this._heroFirstMainSectionCached = null;
  }

  getFirstMainShopifySection(main) {
    if (!main) return null;
    for (const child of main.children) {
      if (
        child?.classList?.contains("shopify-section") ||
        child?.id?.startsWith("shopify-section-")
      ) {
        // Empty spacer / device-hidden sections render nothing, so the hero below is visually first.
        if (
          child.offsetHeight === 0 &&
          !child.querySelector("[data-sticky-header-hero]")
        ) {
          continue;
        }
        return child;
      }
    }
    return null;
  }

  isHeroFirstMainSection() {
    if (this._heroFirstMainSectionCached !== null) {
      return this._heroFirstMainSectionCached;
    }
    const main =
      document.querySelector("#main-content") || document.querySelector("main");
    if (!main) return false;

    const first = this.getFirstMainShopifySection(main);
    if (!first) return false;

    const result = !!first.querySelector("[data-sticky-header-hero]");
    this._heroFirstMainSectionCached = result;
    return result;
  }

  /**
   * True when the theme is configured for a header that overlays the first hero (`data-transparent`
   * from Liquid, e.g. transparent menu / menu-left layout). Not the same as the
   * `wt-header--transparent` class, which scroll observers may toggle independently.
   */
  isTransparentHeaderLayout() {
    return (
      this.dataset.transparent === "true" ||
      this.hasAttribute("data-transparent-hero-auto")
    );
  }

  /**
   * When the first main section is a hero and this bar uses a transparent menu layout (Liquid
   * `data-transparent`), mirror transparent-header CSS on collection and other templates.
   */
  syncMainFirstStickyHeaderHeroClass() {
    const heroFirst = this.isHeroFirstMainSection();
    // Transparent colours are only legible over a hero; otherwise the bar sits on the page background.
    if (this.dataset.transparent === "true") {
      this.classList.toggle("wt-header--transparent", heroFirst);
    }
    if (heroFirst && this.isTransparentHeaderLayout()) {
      document.body.classList.add(this.mainFirstHeroBodyClass);
    } else {
      document.body.classList.remove(this.mainFirstHeroBodyClass);
    }
  }

  shouldPullHeroUnderTransparentHeader() {
    if (!this.isHeroFirstMainSection()) return false;
    return this.isTransparentHeaderLayout();
  }

  clearHeroPullUnderHeader() {
    if (this.heroOverlapResizeObserver) {
      this.heroOverlapResizeObserver.disconnect();
      this.heroOverlapResizeObserver = null;
    }
    if (this.heroOverlapSection) {
      this.heroOverlapSection.style.marginTop = "";
      this.heroOverlapSection = null;
    }
  }

  /**
   * Pull the first main hero section up so its media sits behind the transparent header
   * (otherwise the page body background shows through the clear bar).
   */
  syncHeroPullUnderHeader() {
    this.clearHeroPullUnderHeader();

    if (!this.shouldPullHeroUnderTransparentHeader()) return;

    const main =
      document.querySelector("#main-content") || document.querySelector("main");
    const first = this.getFirstMainShopifySection(main);
    if (!first?.querySelector("[data-sticky-header-hero]")) return;

    const headerShell = document.querySelector("header.page-header");
    if (!headerShell) return;

    const apply = () => {
      const announcement = document.querySelector(".wt-announcement");
      const annH = announcement ? announcement.offsetHeight : 0;
      const offset = headerShell.offsetHeight + annH;
      first.style.marginTop = offset > 0 ? `-${offset}px` : "";
    };

    apply();

    this.heroOverlapSection = first;
    this.heroOverlapResizeObserver = new ResizeObserver(apply);
    this.heroOverlapResizeObserver.observe(headerShell);
    const announcement = document.querySelector(".wt-announcement");
    if (announcement) this.heroOverlapResizeObserver.observe(announcement);
  }

  /**
   * v3 + first main hero:
   * - option_3 ("Logo center, drawer menu"): solid bar under the nav (no overlay). Sets
   *   `data-header-solid-bar` so scroll observers cannot re-apply `wt-header--transparent`.
   * - Transparent menu layouts (Liquid `data-transparent="true"`): keep transparency and hero
   *   underlap; do not strip attrs or force a solid bar.
   * Re-run on `load` in case hero detection was early.
   */
  applyV3FirstHeroSolidBar() {
    if (!this.classList.contains("wt-header--v3")) {
      this.removeAttribute("data-header-solid-bar");
      return;
    }
    if (!this.isHeroFirstMainSection()) {
      // Avoid clearing the solid-bar lock on a transient false during load/layout (that was
      // re-enabling wt-header--transparent and the hero negative margin a moment later).
      if (!this.hasAttribute("data-header-solid-bar")) {
        return;
      }
      const el = this;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!el.isConnected) return;
          if (!el.isHeroFirstMainSection()) {
            el.removeAttribute("data-header-solid-bar");
          }
        });
      });
      return;
    }

    if (this.dataset.transparent === "true") {
      this.removeAttribute("data-header-solid-bar");
      return;
    }

    this.setAttribute("data-header-solid-bar", "");
    this.classList.remove("wt-header--transparent");
  }

  /**
   * Legacy: v2 used to auto-enable a transparent bar when the first main section was a hero.
   * option_2 "Logo center, menu below" must stay solid; explicit transparent layouts set
   * `data-transparent` in Liquid. Keep this hook only to clear stale `data-transparent-hero-auto`
   * if present.
   */
  syncAutoTransparentHeroFirst() {
    const heroFirst = this.isHeroFirstMainSection();

    if (!heroFirst || this.dataset.transparent === "true") {
      this.cleanupAutoTransparentHero();
      return;
    }

    if (!this.classList.contains("wt-header--v2")) {
      this.cleanupAutoTransparentHero();
      return;
    }

    this.cleanupAutoTransparentHero();
  }

  cleanupAutoTransparentHero() {
    if (!this.hasAttribute("data-transparent-hero-auto")) return;
    this.removeAttribute("data-transparent-hero-auto");
    if (this.dataset.transparent !== "true") {
      this.classList.remove("wt-header--transparent");
    }
    document.body.classList.remove("hero-first-transparent-header");
  }

  /**
   * Any layout without Liquid `data-transparent` (non–transparent-menu): strip overlay class/body
   * state from stale markup or scripts (e.g. v2 "Logo center, menu below").
   */
  sanitizeNonExplicitTransparentHeader() {
    if (this.dataset.transparent === "true") return;
    this.classList.remove("wt-header--transparent");
    document.body.classList.remove("hero-first-transparent-header");
  }

  /**
   * Sticky boundary: first main section if it is a hero banner; otherwise top sentinel.
   */
  resolveStickyObserverTarget() {
    const main =
      document.querySelector("#main-content") || document.querySelector("main");
    const first = this.getFirstMainShopifySection(main);
    if (first?.querySelector("[data-sticky-header-hero]")) {
      return first;
    }
    return document.querySelector(".sticky-header__threshold");
  }

  measureStickyRootMarginTopPx() {
    const bar = document.querySelector("#header");
    if (!bar) return this.isStickyAlways ? 160 : 100;
    const h = Math.round(bar.getBoundingClientRect().height);
    const clamped = Math.min(Math.max(h, 48), 200);
    return this.isStickyAlways ? Math.max(clamped, 160) : Math.max(clamped, 100);
  }

   enbleStickyHeader() {
    if (!this.header) {
      console.error("Header element not found for enabling sticky header");
      return;
    }

    document.body.classList.add("page-header-sticky");

    let prevScrollpos = window.scrollY;

    const isDesktop = window.matchMedia("(min-width: 1200px)").matches;
    const isMenuBarOpen = () =>
      this.header.classList.contains(this.desktopHeaderWithMenuBarClass);
    const isHeaderWithDesktopNav =
      !document.body.classList.contains("mobile-nav");

    const getDesktopMenuBar = () => document.querySelector(".wt-drawer--nav");

    const calculateNavbarTopMargin = () => {
      const header = document.querySelector("#header");
      const navbar = document.querySelector("#wt-drawer-nav");
      if (!navbar || !header) return 0;

      let marginTop = 0;

      if (navbar.offsetHeight > header.offsetHeight) {
        marginTop = header.offsetHeight - navbar.offsetHeight;
      } else {
        marginTop = Math.abs(navbar.offsetHeight - header.offsetHeight);
      }

      navbar.style.setProperty("--top-margin", `${marginTop}px`);

      return marginTop;
    };

    requestAnimationFrame(() => {
      calculateNavbarTopMargin();
    });

    const stickyHeader = {
      show: () => {
        if (this.header) this.header.classList.add(this.showClass);
        stickyHeader.visible = true;
        stickyHeader.handleBehavior();
        calculateNavbarTopMargin();
      },
      hide: () => {
        if (this.header) this.header.classList.remove(this.showClass);
        stickyHeader.visible = false;
        stickyHeader.handleBehavior();
      },
      enable: () => {
        if (this.header) this.header.classList.add(this.enabledClass);
        stickyHeader.enabled = true;
        stickyHeader.handleBehavior();
      },
      disable: () => {
        if (this.header)
          this.header.classList.remove(this.enabledClass, this.showClass);
        stickyHeader.enabled = false;
        stickyHeader.handleBehavior();
      },
      enabled: false,
      visible: true,
      handleBehavior: () => {
        if (isHeaderWithDesktopNav && isDesktop && this.header) {
          stickyHeader.log();
          const drawer = getDesktopMenuBar();
          const allLLevelsLinks = drawer
            ? drawer.querySelectorAll("a[data-menu-level]")
            : [];
          const onlyLevel1Links = drawer
            ? drawer.querySelectorAll("a[data-menu-level='1']")
            : [];
          if (!isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "-1");
          }
          if (isMenuBarOpen() && stickyHeader.enabled) {
            setTabindex(allLLevelsLinks, "0");
          }
          if (!stickyHeader.enabled) {
            setTabindex(onlyLevel1Links, "0");
            if (this.desktopMenuTrigger)
              setTabindex([this.desktopMenuTrigger], "-1");
          } else if (this.desktopMenuTrigger)
            setTabindex([this.desktopMenuTrigger], "0");
        }
      },
      log: () => {},
    };

    // Save the scroll handler for later removal
    this.scrollHandler = () => {
      if (this.isHeroFirstMainSection() && window.scrollY < 2) {
        stickyHeader.disable();
      }

      const currentScrollPos = window.scrollY;
      if (!this.isStickyAlways) {
        if (prevScrollpos > currentScrollPos) {
          stickyHeader.show();
        } else {
          stickyHeader.hide();
        }
      } else {
        stickyHeader.show();
      }

      prevScrollpos = currentScrollPos;
    };

    window.addEventListener("scroll", this.scrollHandler);

    this.desktopMenuTrigger?.addEventListener("click", (e) => {
      e.preventDefault();
      getDesktopMenuBar()?.classList.toggle("wt-drawer--nav-show");
      this.desktopMenuTrigger?.classList.toggle(
        "wt-header__sticky-menu-trigger--active",
      );
      this.header?.classList.toggle(this.desktopHeaderWithMenuBarClass);
      stickyHeader.handleBehavior();
    });

    const handleStickySentinel = (entries) => {
      entries.forEach(({ isIntersecting }) => {
        if (this.isHeroFirstMainSection() && window.scrollY < 2) {
          stickyHeader.disable();
          return;
        }
        if (isIntersecting) {
          stickyHeader.disable();
        } else {
          stickyHeader.enable();
        }
      });
    };

    const observeTarget = () => {
      const target = this.resolveStickyObserverTarget();
      if (!target) return;

      if (this.stickyHeaderObserver) {
        this.stickyHeaderObserver.disconnect();
        this.stickyHeaderObserver = null;
      }

      const observingHero = !target.classList.contains(
        "sticky-header__threshold",
      );
      const rootMarginTop = observingHero
        ? 0
        : this.measureStickyRootMarginTopPx();

      this.stickyHeaderObserver = new IntersectionObserver(
        handleStickySentinel,
        {
          root: null,
          rootMargin: `-${rootMarginTop}px 0px 0px 0px`,
          threshold: 0,
        },
      );

      this.stickyHeaderObserver.observe(target);

      if (this.isHeroFirstMainSection() && window.scrollY < 2) {
        stickyHeader.disable();
      }
    };

    if (this.isTransparent || this.isHeroFirstMainSection()) {
      requestAnimationFrame(() => {
        requestAnimationFrame(observeTarget);
      });
    } else {
      observeTarget();
    }
  }

  disableStickyHeader() {
    if (this.header) {
      // Remove classes added by sticky header
      this.header.classList.remove(
        this.enabledClass,
        this.showClass,
        this.desktopHeaderWithMenuBarClass,
      );
      document.body.classList.remove("page-header-sticky");
      document.querySelector(".wt-drawer--nav")?.classList.remove(
        "wt-drawer--nav-show",
      );
      this.desktopMenuTrigger?.classList.remove(
        "wt-header__sticky-menu-trigger--active",
      );
    }

    // Remove the scroll event listener
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
      this.scrollHandler = null;
    }

    // Disconnect the IntersectionObserver
    if (this.stickyHeaderObserver) {
      this.stickyHeaderObserver.disconnect();
      this.stickyHeaderObserver = null;
    }
  }

  init() {
    const run = () => {
      this.syncAutoTransparentHeroFirst();
      this.applyV3FirstHeroSolidBar();
      this.sanitizeNonExplicitTransparentHeader();
      this.syncMainFirstStickyHeaderHeroClass();
      this.syncHeroPullUnderHeader();
      if (this.isSticky) {
        this.enbleStickyHeader();
      } else {
        this.disableStickyHeader();
      }
    };

    const runAfterLoad = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.applyV3FirstHeroSolidBar();
          this.sanitizeNonExplicitTransparentHeader();
          this.syncMainFirstStickyHeaderHeroClass();
          this.syncHeroPullUnderHeader();
        });
      });
    };

    // Header markup is above <main> in the document; connectedCallback often runs
    // before #main-content exists, so hero-first detection must wait.
    if (document.readyState === "loading") {
      this.initAbort?.abort();
      this.initAbort = new AbortController();
      document.addEventListener(
        "DOMContentLoaded",
        run,
        { once: true, signal: this.initAbort.signal },
      );
    } else {
      run();
    }

    if (document.readyState === "complete") {
      queueMicrotask(runAfterLoad);
    } else {
      window.addEventListener("load", runAfterLoad, { once: true });
    }
  }
}

customElements.define("page-header", PageHeaderSection);

/** Transparent header + mega below: mega panel `top` must match real #header bar height (not link row). */
(function initTransparentMegaDropdownTop() {
  const shell = () => document.querySelector("header.page-header");
  const bar = () => document.querySelector("#header");
  const hasMega = () => document.querySelector(".transparent-header-mega-menu");

  let ro;

  const sync = () => {
    const s = shell();
    const b = bar();
    if (!s || !b || !hasMega()) return;
    if (!window.matchMedia("(min-width: 999px)").matches) {
      s.style.removeProperty("--transparent-mega-dropdown-top");
      return;
    }
    s.style.setProperty(
      "--transparent-mega-dropdown-top",
      `${Math.round(b.offsetHeight)}px`,
    );
  };

  const bindResizeObserver = () => {
    const b = bar();
    if (!b || !hasMega() || ro) return;
    ro = new ResizeObserver(sync);
    ro.observe(b);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      sync();
      bindResizeObserver();
    });
  } else {
    sync();
    bindResizeObserver();
  }
  window.addEventListener("resize", sync);
  window.addEventListener("load", sync);
})();
